import { useMemo } from 'react';

/**
 * Custom hook to handle price display logic, including price text overrides
 *
 * @param {Object} offer - The offer object containing title, imageUrl, and priceTextOverride
 * @param {string} displayPrice - The formatted display price
 * @param {Object} checkoutConfigData - Optional checkout configuration data with priceTextOverride settings
 * @returns {Object} An object containing display properties and helper functions
 */
export const usePriceDisplay = (offer, displayPrice, checkoutConfigData = null) => {
  // Compute the effective price text override, prioritizing checkout config
  const effectivePriceTextOverride = useMemo(() => {
    if (checkoutConfigData?.priceTextOverrideEnabled && checkoutConfigData?.priceTextOverride) {
      return checkoutConfigData.priceTextOverride;
    }
    return offer?.priceTextOverride || null;
  }, [
    offer?.priceTextOverride,
    checkoutConfigData?.priceTextOverride,
    checkoutConfigData?.priceTextOverrideEnabled,
  ]);

  // Determine if we have a price text override
  const hasPriceTextOverride = useMemo(
    () => Boolean(effectivePriceTextOverride),
    [effectivePriceTextOverride],
  );

  // Compute the effective display price
  const effectiveDisplayPrice = useMemo(
    () => effectivePriceTextOverride || displayPrice,
    [effectivePriceTextOverride, displayPrice],
  );

  return {
    effectiveDisplayPrice,
    hasPriceTextOverride,
    effectivePriceTextOverride,
  };
};

export default usePriceDisplay;
